import React, { useState, useEffect } from 'react';

// Configuration - Easy to change word counts here
const WORD_COUNT_CONFIG = {
  FIRST_SCENES_WORD_COUNT: 16, // First N-1 scenes must have exactly this many words
  LAST_SCENE_WORD_COUNT: 15,   // Last scene must have exactly this many words
};

// Updated: Any non-space token is a word for all languages
const isWordToken = (token) => token.trim().length > 0;

// Updated: Better word counter that works for any language
const getWordCount = (text) => {
  if (!text || typeof text !== 'string') return 0;
  
  // Trim and split by whitespace, then filter out empty strings
  const words = text.trim().split(/\s+/).filter(word => word.length > 0);
  return words.length;
};

// Helper function to check if text contains non-Latin scripts
const containsNonLatinScript = (text) => {
  if (!text) return false;
  // Check for Unicode ranges of Indian languages
  const nonLatinRanges = [
    /[\u0900-\u097F]/, // Devanagari (Hindi, Sanskrit)
    /[\u0C00-\u0C7F]/, // Telugu
    /[\u0C80-\u0CFF]/, // Kannada
    /[\u0B80-\u0BFF]/, // Tamil
    /[\u0D00-\u0D7F]/, // Malayalam
    /[\u0A80-\u0AFF]/, // Gujarati
    /[\u0A00-\u0A7F]/, // Gurmukhi (Punjabi)
    /[\u0B00-\u0B7F]/, // Oriya
    /[\u0980-\u09FF]/, // Bengali
  ];
  
  return nonLatinRanges.some(range => range.test(text));
};

const DialogueEditor = ({ scenes, onProceedToGeneration, onNewChat, aiModel }) => {
  function extractDialogue(fullText) {
    if (!fullText) return '';
    const match = fullText.match(/Dialogue: "(.*?)"/);
    return match ? match[1] : '';
  }

  // Updated truncate function that preserves meaning for all languages
  const truncateToWordCount = (text, wordCount) => {
    if (!text) return text;

    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    
    if (words.length <= wordCount) return text;
    
    // Take only the first 'wordCount' words
    const truncatedWords = words.slice(0, wordCount);
    return truncatedWords.join(' ');
  };

  // Updated pad function that works for all languages
  const padToWordCount = (text, wordCount) => {
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    if (words.length >= wordCount) return text;
    
    const needed = wordCount - words.length;
    
    // Language-neutral filler words
    const fillerWords = ['today', 'now', 'here', 'always', 'together', 'everyone'];
    
    // Detect if this is a non-English language
    const isNonEnglish = containsNonLatinScript(text);
    
    // For non-English languages, use a simple approach - repeat last word
    if (isNonEnglish) {
      const additions = [];
      const lastWord = words[words.length - 1] || 'ఇప్పుడు'; // Default to 'now' in Telugu
      for (let i = 0; i < needed; i++) {
        additions.push(lastWord);
      }
      return text + ' ' + additions.join(' ');
    }
    
    // For English, use the original smart approach
    const meaningfulWords = words.filter(w => 
      !['the', 'a', 'an', 'and', 'or', 'but', 'is', 'are', 'was', 'were'].includes(w.toLowerCase())
    );
    
    const additions = [];
    for (let i = 0; i < needed; i++) {
      if (i < fillerWords.length && meaningfulWords.length === 0) {
        additions.push(fillerWords[i % fillerWords.length]);
      } else if (meaningfulWords.length > 0) {
        const wordToUse = meaningfulWords[i % meaningfulWords.length];
        additions.push(wordToUse);
      } else {
        additions.push(words[words.length - 1]);
      }
    }
    
    return text.trim() + ' ' + additions.join(' ');
  };

  const [editedScenes, setEditedScenes] = useState([]);
  const [languageDetected, setLanguageDetected] = useState('english');

  // Detect language of first scene for UI
  useEffect(() => {
    if (scenes && scenes.length > 0) {
      const firstDialogue = scenes[0].dialogue || extractDialogue(scenes[0].fullText);
      if (containsNonLatinScript(firstDialogue)) {
        setLanguageDetected('non-english');
      } else {
        setLanguageDetected('english');
      }
    }
  }, [scenes]);

  const isSeedance = aiModel === 'seedance';

  // Auto-pad/truncate when scenes are loaded or changed - this runs when component shows
  useEffect(() => {
    if (scenes && scenes.length > 0) {
      const totalScenes = scenes.length;
      const initialized = scenes.map((scene, index) => {
        let dialogue = scene.dialogue || extractDialogue(scene.fullText);

        if (!isSeedance) {
          const isLastScene = index === scenes.length - 1;
          const requiredWordCount = totalScenes === 1
            ? WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT
            : isLastScene
              ? WORD_COUNT_CONFIG.LAST_SCENE_WORD_COUNT
              : WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT;

          const wordCount = getWordCount(dialogue);
          if (wordCount < requiredWordCount) dialogue = padToWordCount(dialogue, requiredWordCount);
          else if (wordCount > requiredWordCount) dialogue = truncateToWordCount(dialogue, requiredWordCount);
        }

        return { ...scene, editedDialogue: dialogue };
      });
      setEditedScenes(initialized);
    }
  }, [scenes]);

  const handleDialogueChange = (sceneNumber, newDialogue) => {
    setEditedScenes(prev => prev.map((scene, index) => {
      if (scene.sceneNumber === sceneNumber) {
        if (isSeedance) return { ...scene, editedDialogue: newDialogue };

        const isLastScene = index === prev.length - 1;
        const requiredWordCount = prev.length === 1
          ? WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT
          : isLastScene
            ? WORD_COUNT_CONFIG.LAST_SCENE_WORD_COUNT
            : WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT;
        const wordCount = getWordCount(newDialogue);
        if (wordCount > requiredWordCount) {
          return { ...scene, editedDialogue: truncateToWordCount(newDialogue, requiredWordCount) };
        }
        return { ...scene, editedDialogue: newDialogue };
      }
      return scene;
    }));
  };

  // Dynamic validation: First N-1 scenes = 16 words, Last scene = 15 words
  const allValid = isSeedance || editedScenes.every((scene, index) => {
    const wordCount = getWordCount(scene.editedDialogue);
    const isLastScene = index === editedScenes.length - 1;
    const requiredWordCount = editedScenes.length === 1
      ? WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT
      : isLastScene
        ? WORD_COUNT_CONFIG.LAST_SCENE_WORD_COUNT
        : WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT;
    return wordCount === requiredWordCount;
  });

  // Get required word count for a specific scene
  const getRequiredWordCount = (sceneIndex) => {
    const isLastScene = sceneIndex === editedScenes.length - 1;
    if (editedScenes.length === 1) {
      return WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT;
    }
    
    return isLastScene
      ? WORD_COUNT_CONFIG.LAST_SCENE_WORD_COUNT
      : WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT;
  };

  // Check if a scene is valid
  const isSceneValid = (sceneIndex) => {
    if (isSeedance) return true;
    const scene = editedScenes[sceneIndex];
    const wordCount = getWordCount(scene.editedDialogue);
    const requiredWordCount = getRequiredWordCount(sceneIndex);
    return wordCount === requiredWordCount;
  };

  const handleProceed = () => {
    if (allValid && onProceedToGeneration) {
      onProceedToGeneration(editedScenes);
    }
  };

  return (
    <div className="w-full bg-white rounded-xl border border-gray-200 p-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">📝 Edit Scene Dialogues</h3>
            <p className="text-sm text-gray-600">
              {!isSeedance && (
                <>
                  {editedScenes.length === 1 && `This scene must have exactly ${WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT} words.`}
                  {editedScenes.length === 2 && `Scene 1 requires exactly ${WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT} words. Scene 2 requires ${WORD_COUNT_CONFIG.LAST_SCENE_WORD_COUNT} words.`}
                  {editedScenes.length > 2 && `Scenes 1–${editedScenes.length - 1} require exactly ${WORD_COUNT_CONFIG.FIRST_SCENES_WORD_COUNT} words. Scene ${editedScenes.length} requires ${WORD_COUNT_CONFIG.LAST_SCENE_WORD_COUNT} words.`}
                </>
              )}
              {isSeedance && 'Review and edit the dialogue for each scene.'}
            </p>
            {languageDetected === 'non-english' && (
              <p className="text-xs text-blue-600 mt-1">
                🌐 Non-English language detected - word counting works by spaces
              </p>
            )}
          </div>
          {onNewChat && (
            <button
              onClick={onNewChat}
              className="ml-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors border border-gray-300 whitespace-nowrap"
              title="Start a new video project"
            >
              ➕ New Project
            </button>
          )}
        </div>
      </div>

      {/* Scenes Grid - Compact layout to avoid scrolling */}
      <div className="grid grid-cols-1 gap-4 mb-6">
        {editedScenes.map((scene, index) => {
          const wordCount = getWordCount(scene.editedDialogue);
          const requiredWordCount = getRequiredWordCount(index);
          const isValid = isSceneValid(index);
          const isLastScene = index === editedScenes.length - 1;

          return (
            <div key={scene.sceneNumber} className="bg-gray-50 rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-base font-semibold text-gray-900">
                  Scene {scene.sceneNumber}
                  {isLastScene && editedScenes.length > 1 && (
                    <span className="ml-2 text-xs text-gray-500 font-normal">Last</span>
                  )}
                </h4>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  isValid 
                    ? 'bg-blue-100 text-primary' 
                    : 'bg-orange-100 text-orange-600'
                }`}>
                  {wordCount} words {!isSeedance && `/ ${requiredWordCount}`} {isValid ? '✓' : ''}
                </span>
              </div>

              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Dialogue:{!isSeedance && <span className="text-gray-500 font-normal"> (Must be exactly {requiredWordCount} words)</span>}
                </label>
                <textarea
                  value={scene.editedDialogue}
                  onChange={(e) => handleDialogueChange(scene.sceneNumber, e.target.value)}
                  rows={2}
                  placeholder={`Enter dialogue for this scene (exactly ${requiredWordCount} words)...`}
                  className={`w-full px-3 py-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
                    isValid 
                      ? 'border-gray-300 bg-white' 
                      : 'border-orange-300 bg-orange-50'
                  }`}
                  style={containsNonLatinScript(scene.editedDialogue) ? { fontFamily: 'system-ui, -apple-system, sans-serif' } : {}}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex flex-col items-center gap-3 pt-4 border-t border-gray-200">
        <button
          className={`px-6 py-3 rounded-lg font-semibold text-base transition-all ${
            allValid
              ? 'bg-primary text-white hover:bg-primary-hover shadow-md hover:shadow-lg'
              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
          }`}
          onClick={handleProceed}
          disabled={!allValid}
        >
          {allValid ? '✓ Proceed to Video Generation' : '⚠ Fix dialogues to continue'}
        </button>
      </div>
    </div>
  );
};

export default DialogueEditor;